import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type OverviewTriage, type TriageFlag, type TriageTier } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import type { QueryState } from '../../useQuery';
import { EmptyState, QueryBlock } from '../../ui/states';
import { TIER_INK } from './OperatorTriage';
import './triage.css';

// A port of the Console V3 design's Facility Admin Triage section (faSecIsTriage, faT* in
// "Aithora ConsoleV3.dc.html"): the building's flagged residents, who is carrying whom,
// and the clocks the building owns. Reads GET /overview/triage (PROPOSED, building
// scope from the session); Assign / Reassign is POST /overview/triage/:id/assign.

const RANK: Record<TriageTier, number> = { HIGH: 0, ELEVATED: 1, WATCH: 2, LOW: 2 };
const TONE = { act: '#B3261E', watch: '#4A5866' } as const;

/** Flagged residents nobody has been placed with (the number on the Triage tab). */
export function unplacedCount(t: OverviewTriage | null, placed: Record<string, { userId: string; name: string }> = {}): number | null {
  return t ? t.flags.filter((f) => !(placed[f.id] ?? f.owner)).length : null;
}

export function AdminTriage({ q, placed, onPlaced }: { q: QueryState<OverviewTriage>; placed: Record<string, { userId: string; name: string }>; onPlaced: (id: string, owner: { userId: string; name: string }) => void }) {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const demoBuilding = useDemoBuilding();
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const t = q.data;
  const staff = t?.staff ?? [];
  const ownerOf = (f: TriageFlag) => placed[f.id] ?? f.owner;
  // Placed this session last, then unowned before owned, then highest tier first (the design's order).
  const rows = t
    ? t.flags.slice().sort((a, b) => (placed[a.id] ? 1 : 0) - (placed[b.id] ? 1 : 0) || (a.owner ? 1 : 0) - (b.owner ? 1 : 0) || RANK[a.tier] - RANK[b.tier])
    : [];
  const unplaced = rows.filter((r) => !ownerOf(r));
  const highUnplaced = unplaced.filter((r) => r.tier === 'HIGH');
  const loadOf = (userId: string | null) => (userId === null ? unplaced.length : rows.filter((r) => ownerOf(r)?.userId === userId).length);
  const lanes = [...staff.map((s) => ({ key: s.userId, who: s.name, note: s.role, n: loadOf(s.userId), none: false })), { key: 'none', who: 'Unassigned', note: 'nobody has these', n: loadOf(null), none: true }];
  const maxLoad = Math.max(1, ...lanes.map((l) => l.n));
  const heaviest = staff.length ? Math.max(...staff.map((s) => loadOf(s.userId))) : 0;
  // With every detector switched off nobody is scored: that is "not scored", never
  // "nobody is flagged" or "every flagged resident has a name". And an emergency nobody
  // has claimed (the clock below, the To do list) leads the verdict: it outranks a flag.
  const scored = !t || t.detectors > 0;
  const unclaimed = t?.clocks?.find((c) => c.k === 'emergencies nobody has claimed')?.n ?? 0;
  const emergencyLead = unclaimed ? `${unclaimed} ${unclaimed === 1 ? 'emergency' : 'emergencies'} nobody has claimed. ` : '';
  const verdict =
    emergencyLead +
    (!scored
      ? 'No detector is switched on here, so nobody is scored for deterioration.'
      : highUnplaced.length
        ? `${highUnplaced.length}${highUnplaced.length === 1 ? ' high-tier resident has nobody on them.' : ' high-tier residents have nobody on them.'}`
        : unplaced.length
          ? `${unplaced.length} flagged residents are unassigned.`
          : 'Every flagged resident has a name against them.');
  const tone = unclaimed || highUnplaced.length ? 'act' : unplaced.length || !scored ? 'watch' : 'good';

  const assign = async (f: TriageFlag) => {
    if (!staff.length) return;
    const cur = ownerOf(f);
    const i = cur ? staff.findIndex((s) => s.userId === cur.userId) : -1;
    const next = staff[(i + 1) % staff.length];
    setBusy(f.id);
    try {
      const r = await api.triageAssign(client, { params: { id: f.id }, body: { userId: next.userId } });
      onPlaced(f.id, r.owner);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="tri tri--fa" data-testid="overview-admin-triage">
      <QueryBlock q={q} rows={8}>
        {(d) => (
          <>
            <div className="tri__top">
              <span className="tri__lead">
                <span className={`tri__verdict tri--${tone}`}>{verdict}</span>
                <span className="tri__sub">
                  {unplaced.length ? 'Missed care follows load, not effort. Place these before the next round rather than after it.' : 'Load is distributed. The clocks below are what is left to defend.'}
                </span>
              </span>
              <span className="tri__counts">
                {[
                  { v: rows.filter((r) => r.tier === 'HIGH').length, k: 'high tier today', tone: 'act' },
                  { v: unplaced.length, k: 'unassigned', tone: unplaced.length ? 'act' : 'good' },
                  { v: heaviest, k: 'heaviest caseload', tone: 'watch' },
                ].map((c) => (
                  <span key={c.k} className="tri__count">
                    <span className={`tri__cv tri--${c.tone}`}>{c.v}</span>
                    <span className="tri__ck">{c.k}</span>
                  </span>
                ))}
              </span>
            </div>

            <div className="tri__min">
              <div className="tri__head">
                <span className="tri__title">Who is carrying what</span>
                <span className="tri__meta">{`${d.residents ?? '–'} residents in the building · ${scored ? `${rows.length} flagged by the detectors today` : 'not scored: no detector is on'}`}</span>
              </div>
              <div className="tri__load">
                {lanes.map((l) => {
                  const col = l.none ? (l.n ? '#B3261E' : '#1F6B52') : l.n >= 3 ? '#4A5866' : '#22272E';
                  return (
                    <span key={l.key} className="tri__ld" style={{ boxShadow: `inset 3px 0 0 ${col}` }}>
                      <span className="tri__ldh">
                        <span className="tri__ldwho">{l.who}</span>
                        <span className="tri__ldn" style={{ color: col }}>
                          {l.n}
                        </span>
                      </span>
                      <span className="tri__ldbar">
                        <i style={{ width: `${Math.round((l.n / maxLoad) * 100)}%`, background: col }} />
                      </span>
                      <span className="tri__ldnote">{l.note}</span>
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="tri__min">
              <div className="tri__head">
                <span className="tri__title">Residents to place</span>
                <span className="tri__meta">unassigned first · tap Why to see what the indicator is reading</span>
              </div>
              {!scored ? (
                <EmptyState compact title="Nobody is scored: no detector is switched on" body={d.reason ?? undefined} />
              ) : rows.length === 0 ? (
                <EmptyState compact title="Nobody in the building is flagged right now" />
              ) : (
                <div className="tri__rows">
                  {rows.map((f) => {
                    const ink = TIER_INK[f.tier];
                    const owner = ownerOf(f);
                    const isOpen = open === f.id;
                    return (
                      <div key={f.id} className="tri__place" style={{ boxShadow: `inset 3px 0 0 ${ink}`, background: owner ? undefined : `${ink}0A` }}>
                        <div className="tri__prow">
                          <span className="tri__pwho">
                            <span className="tri__name">{f.name}</span>
                            <span className="tri__room">{`Room ${f.room ?? '–'}`}</span>
                          </span>
                          <span className="tri__tier" style={{ color: ink, background: `${ink}1A` }}>
                            {f.tier}
                          </span>
                          <span className="tri__pwhat">
                            <span className="tri__det">{f.detector}</span>
                            <span className="tri__why">{f.why}</span>
                          </span>
                          <span className="tri__pwin" style={{ color: f.tier === 'HIGH' ? '#B3261E' : '#4A5866' }}>
                            {[f.outlook, f.actWithin].filter(Boolean).join(' · ')}
                          </span>
                          <span className={`tri__owner${owner ? '' : ' tri__owner--none'}`}>{owner ? owner.name : 'Unassigned'}</span>
                          <button type="button" className="tri__why-btn" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : f.id)}>
                            {isOpen ? 'Hide' : 'Why'}
                          </button>
                          <button
                            type="button"
                            className={`tri__act${owner ? '' : ' tri__act--claim'}`}
                            disabled={roReason !== null || busy === f.id || staff.length === 0}
                            title={roReason ?? undefined}
                            onClick={() => assign(f)}
                          >
                            {owner ? 'Reassign' : 'Assign'}
                          </button>
                        </div>
                        {isOpen && (
                          <div className="tri__contrib">
                            {(f.contributors ?? []).map((c) => (
                              <span key={c.label} className="tri__cb">
                                <span className="tri__cbl">{c.label}</span>
                                <span className="tri__cbbar">
                                  <i style={{ width: `${c.weight}%`, background: ink }} />
                                </span>
                              </span>
                            ))}
                            <span className="tri__basis">{`Read against ${f.name}’s own 90 days, not against other residents. Advisory only, a person decides.`}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="tri__min">
              <div className="tri__head">
                <span className="tri__title">Clocks you own</span>
                <span className="tri__meta">what stops being defensible if it slips</span>
              </div>
              <div className="tri__clocks">
                {(d.clocks ?? []).map((c) => (
                  <Link key={c.k} to={c.to} className="tri__clock" style={{ boxShadow: `inset 3px 0 0 ${TONE[c.tone]}` }}>
                    <span className="tri__clh">
                      <span className="tri__clv" style={{ color: TONE[c.tone] }}>
                        {c.n}
                      </span>
                      <span className="tri__clk">{c.k}</span>
                    </span>
                    <span className="tri__cln">{c.note}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="tri__fanote">
              {/* A sample building (living demo scope) reads like any other building: no SAMPLE word here. */}
              {d.real || demoBuilding
                ? 'The eight detectors run per resident. Each is a wellness indicator against that resident’s own baseline, not a diagnosis, not a prediction of a named event, and never an automatic action.'
                : 'SAMPLE. The eight detectors run per resident; each is a wellness indicator against that resident’s own baseline, not a diagnosis, not a prediction of a named event, and never an automatic action.'}
            </div>
          </>
        )}
      </QueryBlock>
    </div>
  );
}
